import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    IQueueItemDto,
    IQueueItemRow,
    QueueItemPriority,
} from '@models';
import {
    FileDownloadService,
    MapperService,
} from '@services';
import {
    CSVParser,
    ICSVParseError,
    ICSVParseErrorCode,
    ICSVParseErrorType,
    ICSVParseResult,
} from '@services/csv-parser.service';
import { SUPPORTED_DATE_FORMATS } from '@services/generics/constants';
import { ODataService } from '@services/generics/odata.service';
import { isEmptyObject } from '@utils/audit.utils';

import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    BulkCommitType,
    CSVFields,
    IBulkAddQueueItemsParameters,
    IBulkAddQueueItemsResponse,
    ICSVQueueItemRow,
    ItemPriority,
    ItemPriorityValues,
} from './bulk-add-items.types';

const OUTPUT_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const KNOWN_FIELDS = ['Reference', 'Deadline', 'Priority', 'Postpone'];

@Injectable({
    providedIn: 'root',
})
export class BulkAddItemsModalService extends ODataService<IQueueItemDto, number> {
    protected _endpoint = '/odata/Queues/UiPathODataSvc.BulkAddQueueItems';
    public get supportedDateFormats() {
        return SUPPORTED_DATE_FORMATS;
    }
    constructor(
        http: HttpClient,
        private _parser: CSVParser<CSVFields>,
        private _downloadService: FileDownloadService,
        private _appState: AppStateFacade,
    ) {
        super(http, MapperService.queueItemDtoMap);
    }

    public parseFile(file: File): Observable<ICSVParseResult<CSVFields>> {
        return this._parser.parseFile(
            file,
            {
                header: true,
                skipEmptyLines: true,
                dynamicTyping: true,
                maxFileSize: 2 * 1024 * 1024 * 1024,
                maxRows: this._appState.settings.get(Setting.uploadQueuesMaxNumberOfItems) || 0,
            },
        ).pipe(
            map(({ data, errors, meta }: ICSVParseResult<CSVFields>) => ({
                meta,
                // this._rowMapper mutates the errors argument.
                data: data.map((row: ICSVQueueItemRow, i: number) => this._rowMapper(row, i, errors)),
                // sort and switch to 1-indexed rows.
                errors: errors.map(err => ({ ...err, row: err.row + 1 })).sort((err1, err2) => err1.row - err2.row),
            })),
        );
    }

    public downloadFile(originalFilename: string, filenameSuffix: string, items: any[]) {
        let filename: string;
        const mapItems = items.map(row => this._reverseMapToCsv(row));
        const csvString = this._parser.generateFile(mapItems);

        originalFilename = originalFilename.toLowerCase();

        if (originalFilename.slice(-4) === '.csv') {
            filename = `${originalFilename.slice(0, -4)} - ${filenameSuffix}.csv`;
        } else {
            filename = `${filenameSuffix}.csv`;
        }

        this._downloadService.downloadStringAsFile(filename, 'text/csv', csvString);
    }

    public bulkAddQueueItems(
        queueItems: any[],
        queueName: string,
        commitType: BulkCommitType = BulkCommitType.StopOnFirstFailure,
    ) {
        const payload: IBulkAddQueueItemsParameters = {
            queueItems: queueItems.map(item => this._toPoco(item)),
            queueName,
            commitType,
        };

        return this._http.post(
            this._endpoint,
            JSON.stringify(payload),
        ).pipe(
            map(response => this._toDto(response, MapperService.bulkQueueItemResponseMap) as unknown as IBulkAddQueueItemsResponse),
        );
    }

    private _parseDate(row: number, errors: ICSVParseError<CSVFields>[], field: CSVFields, dateString?: string) {
        if (!dateString) { return; }

        const parsedDate = moment.tz(
            dateString,
            SUPPORTED_DATE_FORMATS,
            true,
            this._appState.settings.get(Setting.timezone)!,
        );

        if (parsedDate.isValid()) {
            return parsedDate;
        }

        errors.push({
            type: ICSVParseErrorType.FieldError,
            code: ICSVParseErrorCode.InvalidData,
            row,
            field,
        });
    }

    private _parseSpecificContent(row: ICSVQueueItemRow) {
        const specificContent = Object.keys(row)
            .filter(key =>
                !KNOWN_FIELDS.includes(key) &&
                key !== '',
            )
            .reduce((obj, key) => {
                obj[key] = row[key];
                return obj;
            }, {} as Record<string, number | boolean | string | undefined>);

        if (isEmptyObject(specificContent)) { return void 0; }

        return specificContent;
    }

    private _parsePriority(
        index: number,
        errors: ICSVParseError<CSVFields>[],
        priority?: number | string,
    ): QueueItemPriority | undefined {
        if (!priority) { return; }

        const isStringValue = isNaN(Number(priority));

        let safePriority: ItemPriorityValues;

        if (isStringValue) {
            safePriority = (priority as string).toLowerCase().trim() as keyof typeof ItemPriority;
        } else {
            safePriority = Number(priority) as 1 | 2 | 3;
        }

        if (ItemPriority[safePriority] != null) {
            return QueueItemPriority[(
                isStringValue ?
                    safePriority :
                    ItemPriority[safePriority]
            ) as keyof typeof ItemPriority];
        }
        errors.push({
            type: ICSVParseErrorType.FieldError,
            code: ICSVParseErrorCode.InvalidData,
            row: index,
            field: CSVFields.Priority,
        });
    }

    private _rowMapper(row: ICSVQueueItemRow, index: number, errors: ICSVParseError<CSVFields>[]): IQueueItemRow {
        return {
            reference: row.Reference != null ? `${row.Reference}` : row.Reference,
            specificContent: this._parseSpecificContent(row),
            dueDate: this._parseDate(index, errors, CSVFields.Deadline, row.Deadline),
            deferDate: this._parseDate(index, errors, CSVFields.Postpone, row.Postpone),
            priority: this._parsePriority(index, errors, row.Priority),
        };
    }

    private _reverseMapToCsv(row: IQueueItemRow): ICSVQueueItemRow {
        const returnedData: ICSVQueueItemRow = {};

        if (row.reference) {
            returnedData.Reference = row.reference;
        }

        if (row.dueDate) {
            returnedData.Deadline = row.dueDate.format(OUTPUT_DATE_FORMAT);
        }

        if (row.priority) {
            returnedData.Priority = row.priority;
        }

        if (row.specificContent) {
            Object.keys(row.specificContent).forEach(key => returnedData[key] = row.specificContent![key]);
        }

        return returnedData;
    }
}
